import React, { useState, useRef, useEffect } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useTrendChart } from "../hooks/useDashboardData";

interface TrendChartProps {
  filters: any;
  categoryOptions?: string[];
}

const LINE_COLORS = [
  "#4692DF", "#165697", "#686767", "#76B7B2",
  "#59A14F", "#EDC948", "#B07AA1",
];

const isSalesRevenue = (name: string) => name.toLowerCase().includes("sales revenue");

// ── Compact dropdown (same sdd-* classes as SidebarDropdown) ─────────────────
const CompactDropdown: React.FC<{ options: string[]; value: string; onChange: (v: string) => void }> = ({
  options, value, onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", minWidth: 120, flexShrink: 0 }}>
      <div
        className={`sdd-control${isOpen ? " sdd-control--open" : ""}`}
        onClick={() => setIsOpen((o) => !o)}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="sdd-value">{value}</span>
        <svg className={`sdd-chevron${isOpen ? " sdd-chevron--up" : ""}`} width="11" height="11" viewBox="0 0 11 11" fill="none">
          <path d="M2 4L5.5 7.5L9 4" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {isOpen && (
        <ul className="sdd-menu" style={{ left: 0, right: 0 }} role="listbox">
          {options.map((opt) => (
            <li
              key={opt}
              className={`sdd-option${opt === value ? " sdd-option--active" : ""}`}
              role="option"
              aria-selected={opt === value}
              onClick={() => { setIsOpen(false); onChange(opt); }}
            >
              <span className="sdd-option-check">
                {opt === value && (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M1.5 5L3.8 7.5L8.5 2" stroke="#1F8CF9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── Tooltip ───────────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-label" style={{ marginBottom: 4 }}>{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ fontSize: 11, color: "#64748b", margin: "1px 0" }}>
          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: p.color, marginRight: 5 }} />
          {p.name}: <strong style={{ color: p.color }}>${p.value}M</strong>
        </p>
      ))}
    </div>
  );
};

// ── Single area chart panel with dual Y-axes ──────────────────────────────────
interface AccountChartProps {
  chartId: string;
  title: string;
  data: any[];
  accounts: string[];
}

const AccountAreaChart: React.FC<AccountChartProps> = ({ chartId, title, data, accounts }) => {
  const hasSales = accounts.some(isSalesRevenue);

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4, textAlign: "center" }}>
        {title}
      </div>
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={data} margin={{ top: 5, right: hasSales ? 50 : 10, left: 5, bottom: 5 }}>
          <defs>
            {accounts.map((acc, i) => (
              <linearGradient key={acc} id={`${chartId}-grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={LINE_COLORS[i % LINE_COLORS.length]} stopOpacity={0.25} />
                <stop offset="95%" stopColor={LINE_COLORS[i % LINE_COLORS.length]} stopOpacity={0.02} />
              </linearGradient>
            ))}
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#DFE2E7" strokeOpacity={0.3} vertical={false} />
          <XAxis dataKey="quarter" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />

          {/* Left axis — Sales Revenue scale */}
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 10, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `$${v}M`}
            width={48}
          />

          {/* Right axis — smaller accounts scale (independent) */}
          {hasSales && (
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[0, "dataMax"]}
              tick={{ fontSize: 10, fill: "#94a3b8" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `$${v}M`}
              width={48}
            />
          )}

          <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#e2e8f0", strokeWidth: 1 }} />

          {accounts.map((acc, i) => (
            <Area
              key={acc}
              yAxisId={isSalesRevenue(acc) ? "left" : (hasSales ? "right" : "left")}
              type="monotone"
              dataKey={acc}
              name={acc}
              stroke={LINE_COLORS[i % LINE_COLORS.length]}
              fill={`url(#${chartId}-grad-${i})`}
              strokeWidth={3}
              fillOpacity={0.25}
              strokeOpacity={0.75}
              dot={{ r: 3, fill: LINE_COLORS[i % LINE_COLORS.length], strokeWidth: 0 }}
              activeDot={{ r: 4 }}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

type ChartView = "both" | "budget" | "actuals";

const TOGGLE_OPTIONS: { value: ChartView; label: string }[] = [
  { value: "both",    label: "Both"    },
  { value: "budget",  label: "Budget"  },
  { value: "actuals", label: "Actuals" },
];

// ── Main component ────────────────────────────────────────────────────────────
const TrendChart: React.FC<TrendChartProps> = ({
  filters,
  categoryOptions = ["All", "EXPENSE", "REVENUE"],
}) => {
  const [subCategory, setSubCategory] = useState("All");
  const [chartView, setChartView]     = useState<ChartView>("budget");

  const mergedFilters = { ...filters, accountCategory: subCategory };
  const { data, isLoading } = useTrendChart(mergedFilters);

  const accounts     = data?.accounts    ?? [];
  const budgetChart  = data?.budgetChart  ?? { title: "Budget by Account",  data: [] };
  const actualsChart = data?.actualsChart ?? { title: "Actuals by Account", data: [] };
  const isEmpty      = accounts.length === 0;

  return (
    <div>
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="chart-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <div className="chart-title">{data?.title ?? "GL Account Budget vs Actuals by Quarter"}</div>
          <div className="chart-subtitle">{data?.subtitle ?? ""}</div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* ── Toggle: Both / Budget / Actuals ── */}
          <div style={{
            display: "flex", alignItems: "center", height: "38px",
            border: "1.5px solid #DEE1E6", borderRadius: 4,
            overflow: "hidden", flexShrink: 0,
          }}>
            {TOGGLE_OPTIONS.map((opt, idx) => (
              <button
                key={opt.value}
                onClick={() => setChartView(opt.value)}
                style={{
                  padding: "5px 10px",
                  fontSize: 13, fontWeight:chartView === opt.value ?700: 500,
                  height: 37,
                  cursor: "pointer", border: "none", outline: "none",
                  borderLeft: idx > 0 ? "1.5px solid #DEE1E6" : "none",
                  background: "white",
                  color:      chartView === opt.value ? "#165696" : "#64748B",
                  transition: "background 0.15s, color 0.15s",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <CompactDropdown options={categoryOptions} value={subCategory} onChange={setSubCategory} />
        </div>
      </div>

      {/* ── Chart body ────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div style={{ height: 195, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 12 }}>
          Loading…
        </div>
      ) : isEmpty ? (
        <div className="chart-empty" style={{ height: 195 }}>
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <polyline points="4,36 16,22 24,28 34,14 44,20" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            <path d="M4 44h40" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <p className="chart-empty-text">No data available for the selected filters</p>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 12 }}>
            {chartView !== "actuals" && (
              <AccountAreaChart chartId="budget"  title={budgetChart.title}  data={budgetChart.data}  accounts={accounts} />
            )}
            {chartView !== "budget" && (
              <AccountAreaChart chartId="actuals" title={actualsChart.title} data={actualsChart.data} accounts={accounts} />
            )}
          </div>

          {/* Shared legend */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 6, justifyContent: "center" }}>
            {accounts.map((acc: string, i: number) => (
              <div key={acc} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <div style={{ width: 16, height: 3, borderRadius: 2, background: LINE_COLORS[i % LINE_COLORS.length], flexShrink: 0 }} />
                <span style={{ fontSize: 10, color: "#64748b", whiteSpace: "nowrap" }}>{acc}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default TrendChart;
